import React from 'react';
import { FaTimes } from 'react-icons/fa';

const extractItemProperty = (item, keyOrFn, fallback = '') => {
  if (!item) return fallback;
  if (typeof keyOrFn === 'function') return keyOrFn(item);
  if (typeof keyOrFn === 'string' && keyOrFn in item) return item[keyOrFn];
  return fallback;
};

/**
 * Component that displays active filter criteria as dismissible tags/chips with individual remove actions.
 * Accepts either pre-mapped `items` or `selectedValues` paired with `options`.
 *
 * @component
 * @param {Object} props
 * @param {Array<Object|string|number>} [props.items=[]] - Explicit array of items to display as chips.
 * @param {Array<string|number>} [props.selectedValues] - Array of selected keys to resolve against `options`.
 * @param {Array<Object>} [props.options=[]] - List of available options used to resolve `selectedValues` into display labels.
 * @param {string|Function} [props.valueKey='id'] - Property key or extractor function for the option value.
 * @param {string|Function} [props.labelKey='name'] - Property key or extractor function for the option display label.
 * @param {Function} [props.onRemove] - Callback invoked when a chip's remove button (✕) is clicked: `(value, item) => void`.
 * @param {Function} [props.onClearAll] - Optional callback to clear all active filter chips at once.
 * @param {string} [props.label='Active:'] - Prefix label displayed before the chip list (pass empty string or null to hide).
 * @param {string} [props.className=''] - Additional CSS classes applied to the container element.
 *
 * @example
 * <DataFilterChips
 *   selectedValues={selectedRoles}
 *   options={roleOptions}
 *   valueKey="role_id"
 *   labelKey="role_name"
 *   onRemove={(val) => setSelectedRoles(prev => prev.filter(id => id !== val))}
 * />
 */
const DataFilterChips = ({
  items = [],
  selectedValues,
  options = [],
  valueKey = 'id',
  labelKey = 'name',
  onRemove,
  onClearAll,
  label = 'Active:',
  className = ''
}) => {
  let chipList = [];

  if (selectedValues && options.length > 0) {
    const valuesArray = Array.isArray(selectedValues) ? selectedValues : [selectedValues];
    chipList = valuesArray
      .map((val) => {
        const found = options.find((opt) => extractItemProperty(opt, valueKey) === val);
        if (!found) return null;
        return {
          value: val,
          label: extractItemProperty(found, labelKey, String(val))
        };
      })
      .filter(Boolean);
  } else if (Array.isArray(items)) {
    chipList = items
      .map((item) => {
        if (typeof item === 'object' && item !== null) {
          const value = extractItemProperty(item, valueKey) || item.value || item.id;
          const text = extractItemProperty(item, labelKey) || item.label || item.name || String(value);
          return { value, label: text, raw: item };
        }
        return { value: item, label: String(item) };
      })
      .filter(Boolean);
  }

  if (chipList.length === 0) {
    return null;
  }

  return (
    <div className={`flex flex-wrap items-center gap-1.5 pt-1 ${className}`.trim()}>
      {label && (
        <span className="text-xs font-semibold text-gray-400 mr-1">
          {label}
        </span>
      )}

      {chipList.map((chip) => (
        <span
          key={chip.value}
          className="inline-flex items-center gap-1.5 rounded-md border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-medium text-[#3B74BE]"
        >
          <span>{chip.label}</span>
          {typeof onRemove === 'function' && (
            <button
              type="button"
              onClick={() => onRemove(chip.value, chip.raw)}
              className="rounded-full p-0.5 hover:bg-blue-200 text-[#3B74BE] transition-colors focus:outline-none cursor-pointer"
              title={`Remove ${chip.label}`}
              aria-label={`Remove ${chip.label}`}
            >
              <FaTimes className="h-2.5 w-2.5" />
            </button>
          )}
        </span>
      ))}

      {typeof onClearAll === 'function' && chipList.length > 1 && (
        <button
          type="button"
          onClick={onClearAll}
          className="text-xs text-gray-400 hover:text-red-600 underline ml-1 cursor-pointer"
        >
          Clear all
        </button>
      )}
    </div>
  );
};

export default DataFilterChips;
